import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Modal,
  Button,
  Card,
  Space,
  Typography,
  Input,
  Spin,
  Image,
  Empty,
  Row,
  Col,
  Popconfirm,
  message,
  Tag,
  Upload,
  Form,
  Select,
  Avatar,
  List,
  Tooltip,
  Divider,
  Collapse,
  Alert,
} from 'antd';
import {
  PictureOutlined,
  DeleteOutlined,
  CheckOutlined,
  RobotOutlined,
  ReloadOutlined,
  UploadOutlined,
  SaveOutlined,
  HistoryOutlined,
  EyeOutlined,
  EditOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { SourceCharacter, SourceImage, CharacterCheckpoint } from '../../types';
import { sourceStoriesApi, UpdateSourceCharacterRequest } from '../../services/api';
import { useApi, useAsyncOperation } from '../../hooks';
import { formatDate } from '../../utils';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface CharacterImageModalProps {
  visible: boolean;
  character: SourceCharacter;
  storyId: string;
  onClose: () => void;
}

const CharacterImageModal: React.FC<CharacterImageModalProps> = ({
  visible,
  character,
  storyId,
  onClose,
}) => {
  const [visualPrompt, setVisualPrompt] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSuggestingName, setIsSuggestingName] = useState(false);
  const [generatingCheckpointIds, setGeneratingCheckpointIds] = useState<Set<string>>(new Set());
  const [editingCheckpoint, setEditingCheckpoint] = useState<any | null>(null);
  const [infoForm] = Form.useForm();
  const [editCheckpointForm] = Form.useForm();

  const { loading: asyncLoading, execute: executeAsync } = useAsyncOperation();

  // Fetch character images
  const {
    data: imagesData,
    loading: imagesLoading,
    refresh: refreshImages,
  } = useApi(
    useCallback(
      () => (visible ? sourceStoriesApi.getCharacterImages(storyId, character.id) : Promise.resolve({ data: [] })),
      [storyId, character.id, visible]
    ),
    { immediate: visible }
  );

  // Epic 3: Fetch checkpoints
  const {
    data: checkpointsData,
    loading: checkpointsLoading,
    refresh: refreshCheckpoints,
  } = useApi(
    useCallback(
      () => (visible ? sourceStoriesApi.getCharacterCheckpoints(storyId, character.id) : Promise.resolve({ data: [] })),
      [storyId, character.id, visible]
    ),
    { immediate: visible }
  );

  // Auto-poll when any checkpoint is GENERATING
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    const hasGenerating = (checkpointsData?.data || []).some((cp: any) => cp.avatarStatus === 'GENERATING');
    if (hasGenerating && visible) {
      if (!pollingRef.current) {
        pollingRef.current = setInterval(() => refreshCheckpoints(), 5000);
      }
    } else {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [checkpointsData, visible, refreshCheckpoints]);

  const handleGeneratePrompt = async () => {
    await executeAsync(
      async () => {
        const result = await sourceStoriesApi.generateCharacterVisualPrompt(storyId, character.id, additionalInfo);
        setVisualPrompt(result.data.prompt);
      },
      { 
        successMessage: 'Visual prompt generated successfully!',
      }
    );
  };

  const handleGenerateImage = async () => {
    if (!visualPrompt.trim()) {
      message.warning('Please generate or enter a visual prompt first.');
      return;
    }

    setIsGenerating(true);
    try {
      await sourceStoriesApi.generateCharacterImage(storyId, character.id, visualPrompt);
      message.success('Image generated successfully!');
      refreshImages();
    } catch (error: any) {
      message.error(error.message || 'Failed to generate image');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUpload = async (file: File) => {
    await executeAsync(
      async () => {
        await sourceStoriesApi.uploadCharacterImage(storyId, character.id, file);
        refreshImages();
        message.success('Character image uploaded successfully!');
      },
      { 
        successMessage: 'Character image uploaded successfully!' 
      }
    );
  };

  const handleSetAsAvatar = async (imageId: string) => {
    await executeAsync(
      async () => {
        await sourceStoriesApi.setCharacterAvatar(storyId, character.id, imageId);
      },
      { successMessage: 'Avatar updated successfully!' }
    );
    refreshImages();
  };

  const handleSuggestName = async () => {
    const values = infoForm.getFieldsValue();
    if (!values.name) {
      message.warning('Please enter character original name first');
      return;
    }
    
    setIsSuggestingName(true);
    try {
      const result = await sourceStoriesApi.suggestCharacterName(storyId, character.id, {
        name: values.name,
        description: values.description
      });
      if (result.data.suggestedName) {
        infoForm.setFieldsValue({ localizedName: result.data.suggestedName });
        message.success(`AI suggested: ${result.data.suggestedName}`);
      }
    } catch (error) {
      console.error('Failed to suggest name:', error);
      message.error('Failed to suggest name with AI');
    } finally {
      setIsSuggestingName(false);
    }
  };

  const handleGenerateCheckpointAvatar = async (checkpointId: string) => {
    setGeneratingCheckpointIds(prev => new Set(prev).add(checkpointId));
    try {
      const result = await sourceStoriesApi.generateCheckpointAvatar(storyId, checkpointId);
      message.success('Checkpoint avatar generated!');
      refreshCheckpoints();
    } catch (error: any) {
      message.error(error.message || 'Failed to generate checkpoint avatar');
    } finally {
      setGeneratingCheckpointIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(checkpointId);
        return newSet;
      });
    }
  };

  const handleEditCheckpoint = (checkpoint: any) => {
    setEditingCheckpoint(checkpoint);
    editCheckpointForm.setFieldsValue({
      changeDescription: checkpoint.changeDescription,
      fullDescription: checkpoint.fullDescription
    });
  };

  const handleSaveCheckpointEdit = async () => {
    try {
      const values = await editCheckpointForm.validateFields();
      const userId = localStorage.getItem('userId') || 'unknown';
      
      await executeAsync(
        () => sourceStoriesApi.updateCharacterCheckpoint(storyId, editingCheckpoint.id, {
          changeDescription: values.changeDescription,
          fullDescription: values.fullDescription,
          modifiedBy: userId
        }),
        { successMessage: 'Checkpoint updated!' }
      );
      
      setEditingCheckpoint(null);
      refreshCheckpoints();
    } catch (error: any) {
      message.error(error.message || 'Failed to update checkpoint');
    }
  };

  const handleDeleteCheckpoint = async (checkpointId: string) => {
    await executeAsync(
      () => sourceStoriesApi.deleteCharacterCheckpoint(storyId, checkpointId),
      { successMessage: 'Checkpoint deleted!' }
    );
    refreshCheckpoints();
  };

  const handleDeleteImage = async (imageId: string) => {
    await executeAsync(
      () => sourceStoriesApi.deleteCharacterImage(storyId, character.id, imageId),
      { successMessage: 'Image deleted successfully!' }
    );
    refreshImages();
  };

  const images = imagesData?.data || [];
  const hasImages = images.length > 0;
  const isProcessing = isGenerating || asyncLoading;

  const handleUpdateInfo = async (values: any) => {
    const { localizedName, appearanceOverride, ...rest } = values;

    // Convert array of {key, value} to JSON object
    let parsedOverride = null;
    if (appearanceOverride && Array.isArray(appearanceOverride) && appearanceOverride.length > 0) {
      parsedOverride = {};
      appearanceOverride.forEach((item: any) => {
        if (item && item.key && item.value) {
          parsedOverride[item.key] = item.value;
        }
      });
      // If no valid key-value pairs, set to null
      if (Object.keys(parsedOverride).length === 0) {
        parsedOverride = null;
      }
    }

    const finalValues: UpdateSourceCharacterRequest = {
      ...rest,
      appearanceOverride: parsedOverride,
      localizedData: {
        ...(character.localizedData || {}),
        name: localizedName || ''
      }
    };

    await executeAsync(
      () => sourceStoriesApi.updateSourceCharacter(storyId, character.id, finalValues),
      { successMessage: 'Character information updated successfully!' }
    );
  };

  return (
    <Modal
      title={`Character Detail: ${character.name}`}
      open={visible}
      onCancel={isProcessing ? undefined : onClose}
      footer={null}
      width={1400}
      centered
      maskClosable={false}
      keyboard={false}
      styles={{
        body: {
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
          padding: '24px'
        }
      }}
    >
      <Row gutter={24}>
        {/* Left Side: Information */}
        <Col span={10}>
          <Card title="Character Information" size="small">
            <Form
              form={infoForm}
              layout="vertical"
              initialValues={{
                name: character.name,
                role: character.role,
                description: character.description,
                localizedName: character.localizedData?.name,
                appearanceOverride: character.appearanceOverride
                  ? Object.entries(character.appearanceOverride).map(([key, value]) => ({ key, value }))
                  : [],
              }}
              onFinish={handleUpdateInfo}
            >
              <Form.Item
                name="name"
                label="Original Name"
                rules={[{ required: true, message: 'Please enter original name' }]}
              >
                <Input placeholder="Character name in story" />
              </Form.Item>

              <Form.Item
                name="localizedName"
                label="Localized Name"
              >
                <Input 
                  placeholder="Enter name for target market" 
                  suffix={
                    <Button
                      type="text"
                      size="small"
                      icon={<RobotOutlined />}
                      loading={isSuggestingName}
                      onClick={handleSuggestName}
                      title="AI Suggest Name"
                    />
                  }
                />
              </Form.Item>

              <Form.Item
                name="role"
                label="Role"
                rules={[{ required: true, message: 'Please select role' }]}
              >
                <Select placeholder="Select role">
                  <Option value="PROTAGONIST">Protagonist</Option>
                  <Option value="ANTAGONIST">Antagonist</Option>
                  <Option value="SUPPORTING">Supporting</Option>
                  <Option value="BACKGROUND">Background</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="description"
                label="Description"
                extra={<Text type="secondary" style={{ fontSize: 11 }}>Base description (read-only). Use Appearance Override below to change appearance.</Text>}
              >
                <TextArea 
                  rows={3} 
                  placeholder="Describe character personality, background, etc." 
                  disabled
                  style={{ backgroundColor: '#f5f5f5', color: 'rgba(0,0,0,0.65)' }}
                />
              </Form.Item>

              <Divider>
                <Space>
                  <span>Appearance Override</span>
                  <Tag color="orange" style={{ fontSize: 10 }}>Used for Image Generation</Tag>
                </Space>
              </Divider>

             

              <Form.List name="appearanceOverride">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                        <Form.Item
                          {...restField}
                          name={[name, 'key']}
                          rules={[{ required: true, message: 'Key required' }]}
                          style={{ marginBottom: 0, width: 120 }}
                        >
                          <Input placeholder="e.g., face" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'value']}
                          rules={[{ required: true, message: 'Value required' }]}
                          style={{ marginBottom: 0, flex: 1 }}
                        >
                          <Input placeholder="e.g., round with big eyes" />
                        </Form.Item>
                        <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                      </Space>
                    ))}
                    <Form.Item style={{ marginBottom: 8 }}>
                      <Button
                        type="dashed"
                        onClick={() => add()}
                        block
                        icon={<PlusOutlined />}
                      >
                        Add Appearance Field
                      </Button>
                    </Form.Item>
                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 16 }}>
                      Common keys: <Text code style={{ fontSize: 10 }}>face</Text>, <Text code style={{ fontSize: 10 }}>body</Text>, <Text code style={{ fontSize: 10 }}>hair</Text>, <Text code style={{ fontSize: 10 }}>clothing</Text>, <Text code style={{ fontSize: 10 }}>accessories</Text>
                    </div>
                  </>
                )}
              </Form.List>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={asyncLoading}
                  block
                >
                  Save Information
                </Button>
              </Form.Item>
            </Form>
          </Card>

          {/* Epic 3: Checkpoints */}
          <Card
            title={
              <Space>
                <HistoryOutlined />
                <span>Checkpoints ({(checkpointsData?.data || []).length})</span>
              </Space>
            }
            size="small"
            style={{ marginTop: 16 }}
          >
            {checkpointsLoading ? (
              <Spin size="small" />
            ) : (checkpointsData?.data || []).length === 0 ? (
              <Text type="secondary" style={{ fontSize: 12 }}>No checkpoints yet. Auto-detected during panel extraction.</Text>
            ) : (
              <List
                size="small"
                dataSource={checkpointsData?.data || []}
                renderItem={(cp: any) => (
                  <List.Item
                    key={cp.id}
                    actions={[
                      <Tooltip title="Edit description" key="edit">
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => handleEditCheckpoint(cp)}
                        />
                      </Tooltip>,
                      <Tooltip title={cp.avatarStatus === 'GENERATING' ? 'Đang tạo avatar...' : cp.avatarStatus === 'READY' ? 'Regenerate avatar' : cp.avatarStatus === 'FAILED' ? 'Retry generate avatar' : 'Generate avatar for this checkpoint'} key="gen">
                        <Button
                          type={cp.avatarStatus === 'READY' ? 'default' : cp.avatarStatus === 'FAILED' ? 'primary' : 'primary'}
                          danger={cp.avatarStatus === 'FAILED'}
                          size="small"
                          icon={cp.avatarStatus === 'GENERATING' ? <LoadingOutlined spin /> : <PictureOutlined />}
                          onClick={() => handleGenerateCheckpointAvatar(cp.id)}
                          loading={generatingCheckpointIds.has(cp.id)}
                          disabled={cp.avatarStatus === 'GENERATING'}
                        >
                          {cp.avatarStatus === 'READY' ? 'Regen' : cp.avatarStatus === 'GENERATING' ? 'Generating' : cp.avatarStatus === 'FAILED' ? 'Retry' : 'Gen'}
                        </Button>
                      </Tooltip>,
                      <Popconfirm title="Delete checkpoint?" onConfirm={() => handleDeleteCheckpoint(cp.id)} key="del">
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>,
                    ]}
                  >
                    <List.Item.Meta
                      avatar={
                        cp.avatarStatus === 'GENERATING' ? (
                          <Avatar icon={<LoadingOutlined spin />} size={48} style={{ background: '#e6f4ff' }} />
                        ) : cp.avatarUrl ? (
                          <Image
                            src={cp.avatarUrl}
                            width={48}
                            height={48}
                            style={{
                              objectFit: 'cover',
                              objectPosition: 'top',
                              borderRadius: '50%',
                              cursor: 'pointer'
                            }}
                            preview={{
                              mask: <EyeOutlined style={{ fontSize: 16 }} />
                            }}
                          />
                        ) : cp.avatarStatus === 'FAILED' ? (
                          <Avatar icon={<ExclamationCircleOutlined />} size={48} style={{ background: '#fff2f0', color: '#ff4d4f' }} />
                        ) : (
                          <Avatar icon={<HistoryOutlined />} size={48} style={{ background: '#f0f0f0' }} />
                        )
                      }
                      title={
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                          <Tooltip title={cp.changeDescription}>
                            <Text strong style={{ fontSize: 13 }} ellipsis>{cp.changeDescription}</Text>
                          </Tooltip>
                          {cp.effectiveFromPanel && (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              Scene: <Text style={{ fontSize: 11 }}>{cp.effectiveFromPanel.sceneName}</Text>
                              {' · Panel '}{cp.effectiveFromPanel.sceneOrderIndex}
                            </Text>
                          )}
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                          <Collapse ghost size="small">
                            <Collapse.Panel 
                              header={<Text type="secondary" style={{ fontSize: 11 }}>View full description</Text>} 
                              key="1"
                              style={{ padding: 0 }}
                            >
                              <Text style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                                {cp.fullDescription}
                              </Text>
                            </Collapse.Panel>
                          </Collapse>
                          <Space size={8}>
                            <Tag
                              color={cp.avatarStatus === 'READY' ? 'success' : cp.avatarStatus === 'GENERATING' ? 'processing' : cp.avatarStatus === 'FAILED' ? 'error' : 'default'}
                              icon={cp.avatarStatus === 'GENERATING' ? <LoadingOutlined spin /> : undefined}
                              style={{ fontSize: 10, margin: 0 }}
                            >
                              Avatar: {cp.avatarStatus === 'READY' ? 'Ready' : cp.avatarStatus === 'GENERATING' ? 'Generating...' : cp.avatarStatus === 'FAILED' ? 'Failed' : 'Not Generated'}
                            </Tag>
                            <Text type="secondary" style={{ fontSize: 10 }}>
                              {formatDate(cp.createdAt)}
                            </Text>
                          </Space>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>

        {/* Right Side: Visual Identity */}
        <Col span={14}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Step 1: Generate Visual Prompt */}
            <Card 
              title="Step 1: Generate Visual Description" 
              size="small"
            >
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: 8 }}>
                  Thông tin bổ sung (Để điều chỉnh ngoại hình)
                </Text>
                <TextArea
                  rows={2}
                  placeholder="Ví dụ: mặc áo bào đỏ, tóc bạc trắng, cầm kiếm..."
                  value={additionalInfo}
                  onChange={(e) => setAdditionalInfo(e.target.value)}
                  disabled={asyncLoading}
                />
              </div>

              <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: 8 }}>
                AI Visual Prompt (English)
              </Text>
              <TextArea
                rows={4}
                placeholder="Enter detailed visual description in English for AI image generation..."
                value={visualPrompt}
                onChange={(e) => setVisualPrompt(e.target.value)}
                disabled={asyncLoading}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <Button
                  type="primary"
                  size="small"
                  icon={<RobotOutlined />}
                  onClick={handleGeneratePrompt}
                  loading={asyncLoading}
                >
                  AI Suggest Prompt
                </Button>
              </div>
              <Text type="secondary" style={{ fontSize: '12px', marginTop: 8, display: 'block' }}>
                Describe the character's appearance in detail (face, hair, clothing, body type, etc.) in English for optimal AI image generation.
              </Text>
            </Card>

            {/* Step 2: Generate or Upload Image */}
            <Card title="Step 2: Character Image" size="small">
              <Row gutter={16}>
                <Col span={18}>
                  <Button
                    type="primary"
                    icon={<PictureOutlined />}
                    onClick={handleGenerateImage}
                    loading={isGenerating}
                    disabled={!visualPrompt.trim() || asyncLoading}
                    block
                  >
                    {isGenerating ? 'Generating Image...' : 'Generate Image with AI'}
                  </Button>
                </Col>
                <Col span={6}>
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      handleUpload(file);
                      return false;
                    }}
                  >
                    <Button 
                      icon={<UploadOutlined />} 
                      block
                      disabled={isGenerating || asyncLoading}
                    >
                      Upload
                    </Button>
                  </Upload>
                </Col>
              </Row>
            </Card>

            {/* Step 3: Image Gallery */}
            <Card 
              title={`Image Gallery (${images.length})`} 
              size="small"
              extra={
                hasImages && (
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={refreshImages}
                    loading={imagesLoading}
                  >
                    Refresh
                  </Button>
                )
              }
            >
              {imagesLoading ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <Spin />
                </div>
              ) : !hasImages ? (
                <Empty
                  description="No images yet. Generate your first character image above."
                  style={{ padding: '40px 0' }}
                />
              ) : (
                <Row gutter={[16, 16]}>
                  {images.map((image) => (
                    <Col span={6} key={image.id}>
                      <div style={{ position: 'relative', border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                        <div style={{ width: '100%', aspectRatio: '9 / 16', overflow: 'hidden', backgroundColor: '#f0f0f0' }}>
                          <Image
                            src={image.url}
                            alt="Character"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }}
                            preview={{ mask: 'Preview' }}
                          />
                        </div>
                        
                        {character.avatarUrl === image.url && (
                          <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}>
                            <Tag color="green" style={{ margin: 0, boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>Avatar</Tag>
                          </div>
                        )}

                        <div style={{ 
                          position: 'absolute', 
                          bottom: 0, 
                          left: 0, 
                          right: 0, 
                          padding: '8px', 
                          background: 'rgba(255,255,255,0.9)', 
                          backdropFilter: 'blur(4px)',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderTop: '1px solid #f0f0f0'
                        }}>
                          <Space size={4}>
                            <Button
                              type={character.avatarUrl === image.url ? 'primary' : 'text'}
                              size="small"
                              icon={<CheckOutlined />}
                              onClick={() => handleSetAsAvatar(image.id)}
                              disabled={character.avatarUrl === image.url}
                              title="Set as Avatar"
                            />
                            <Popconfirm
                              title="Delete this image?"
                              onConfirm={() => handleDeleteImage(image.id)}
                            >
                              <Button
                                type="text"
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                                title="Delete"
                              />
                            </Popconfirm>
                          </Space>
                          <Text type="secondary" style={{ fontSize: '10px' }}>
                            {formatDate(image.createdAt)}
                          </Text>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              )}
            </Card>
          </Space>
        </Col>
      </Row>

      {/* Edit Checkpoint Modal */}
      <Modal
        title="Edit Checkpoint"
        open={!!editingCheckpoint}
        onOk={handleSaveCheckpointEdit}
        onCancel={() => setEditingCheckpoint(null)}
        okText="Save & Regenerate"
        width={700}
        okButtonProps={{ loading: asyncLoading }}
      >
        {editingCheckpoint && (
          <Form form={editCheckpointForm} layout="vertical">
            <Alert
              message="Edit Checkpoint Description"
              description="Modify the description to improve image generation accuracy. AI will use this updated information when generating the checkpoint avatar."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Form.Item label="Context" style={{ marginBottom: 12 }}>
              <Text type="secondary">
                Scene: <Text strong>{editingCheckpoint.effectiveFromPanel?.scene?.name}</Text> | 
                Panel #{editingCheckpoint.effectiveFromPanel?.panelOrder}
              </Text>
            </Form.Item>

            <Form.Item
              name="changeDescription"
              label="Change Summary"
              rules={[{ required: true, message: 'Change summary is required' }]}
            >
              <Input.TextArea
                rows={2}
                placeholder="Brief change (e.g., 'Teacher uniform with glasses')"
              />
            </Form.Item>

            <Form.Item
              name="fullDescription"
              label="Full Description (Used for Image Generation)"
              rules={[{ required: true, message: 'Full description is required' }]}
              extra="This description is sent to AI for image generation. Be specific about appearance, outfit, accessories, etc."
            >
              <Input.TextArea
                rows={6}
                placeholder="Detailed description used for image generation..."
              />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </Modal>
  );
};

export default CharacterImageModal;
